
import React, { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getEmployeeInfo, updateEmployeePreferences } from '../../store/slices/Requestor/employeeInfoSlice'
import errorIcon from "../../../public/assets/images/errorCircleSmall.svg";
import { LabeledValue } from '../../components/common/LabeledValue'
import { TextInputField } from '../../components/common/FormFields'
import Loader from '../../components/common/Loader'
import ValidationBanner from '../../components/common/ValidationBanner'
import 'card-pattern/dist/main.css'
import plusIcon from '../../../public/assets/images/plus-circle-outline.svg'
import minusIcon from '../../../public/assets/images/minus-circle-solid.svg'
import { getEnvironmentConfig } from '../../utils/detectEnv'

// ============================================================================
// EMAIL VALIDATION HELPER
// ============================================================================

// SCAL's regionCd, per the TORTOWNER.EMPLOYEE region reference (CHAR(1); B = Southern California).
const SCAL_REGION_CD = 'B'

const validateEmail = (email: string): string | null => {
  if (!email) return 'Email is required'
  if (!email.includes('@')) return 'Email must contain @'
  if (!email.includes('.')) return 'Email must contain .'
  if (!new RegExp(/^[^\s@]+@[^\s@.]+\.(org|in|com)$/).exec(email))
    return 'Invalid email format.'
  return null
}

// ============================================================================
// EMAIL CARD SECTION COMPONENT
// ============================================================================

/**
 * Renders and validates the preferred email selection/edit form.
 * @returns The email preference editor section when visible.
 */
export const EmailCardSection: React.FC<{
  showEmailCard: boolean
  setShowEmailCard: (show: boolean) => void
  currentEmail: string
  prefilledAlternateEmail?: string
  emailOptions?: Array<{
    key: string
    label?: string
    id?: string
    checked?: boolean
    requiresNewEmail?: boolean
  }>
  onSave?: (payload: {
    kpSelected: boolean
    alternateSelected: boolean
    alternateEmail?: string
  }) => Promise<void>
  isLoading?: boolean
}> = ({ showEmailCard, setShowEmailCard, currentEmail, prefilledAlternateEmail, emailOptions, onSave, isLoading = false }) => {
  const defaultOptions = [
    {
      key: 'work',
      label: currentEmail,
      id: 'checkbox-email-work',
      checked: true,
      requiresNewEmail: false,
    },
    {
      key: 'alternative',
      label: 'Alternative e-mail address',
      id: 'checkbox-email-alternative',
      checked: false,
      requiresNewEmail: true,
    },
  ]

  const options = emailOptions?.length ? emailOptions : defaultOptions
  
  const [selectedEmails, setSelectedEmails] = useState<Record<string, boolean>>(
    () =>
      options.reduce(
        (acc, opt) => ({ ...acc, [opt.key]: !!opt.checked }),
        {} as Record<string, boolean>
      )
  )
  
  const [newEmail, setNewEmail] = useState(() => {
    // Pre-populate with prefilledAlternateEmail if it exists
    return prefilledAlternateEmail || ''
  })
  const [reenteredEmail, setReenteredEmail] = useState(() => {
    // Pre-populate with prefilledAlternateEmail if it exists
    return prefilledAlternateEmail || ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const resetSelected = () =>
    options.reduce(
      (acc, opt) => ({ ...acc, [opt.key]: !!opt.checked }),
      {} as Record<string, boolean>
    )

  const handleReset = () => {
    setShowEmailCard(false)
    setNewEmail('')
    setReenteredEmail('')
    setSelectedEmails(resetSelected())
    setErrors({})
  }

  const handleEmailCheckboxChange = (emailType: string, checked: boolean) => {
    setSelectedEmails((prev) => ({ ...prev, [emailType]: checked }))
  }

  const validateEmails = (): boolean => {
    const newErrors: Record<string, string> = {}
    
    // Check if at least one email is selected
    const hasSelectedEmail = Object.values(selectedEmails).some(Boolean)
    if (!hasSelectedEmail) {
      return true // Allow save even without selection - API will handle it
    }
    
    // Only validate new email input if the alternative is selected
    if (selectedEmails['alternate']) {
      const newEmailError = validateEmail(newEmail)
      if (newEmailError) newErrors.newEmail = newEmailError

      if (!reenteredEmail) {
        newErrors.reenteredEmail = 'Confirmation email is required'
      } else if (newEmail !== reenteredEmail) {
        newErrors.reenteredEmail = 'Emails do not match'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (validateEmails()) {
      if (onSave) {
        onSave({
          kpSelected: selectedEmails['kp'] || false,
          alternateSelected: selectedEmails['alternate'] || false,
          ...(selectedEmails['alternate'] && newEmail ? { alternateEmail: newEmail } : {}),
        }).then(() => {
          handleReset()
        }).catch(() => {
          // Error is handled by Redux, keep form open
        })
      } else {
        handleReset()
      }
    }
  }

  if (!showEmailCard) {
    return null
  }

  return (
    <div className={`w-full ml-0 desktop:ml-8! mt-0`}>
      <p className="p3 -book mb-4">
       Select your preferred address for Time Off Request e-mail notifications.
      </p>

      <div>
        <div className="flex flex-col gap-1 mb-3">
          {options.map((opt) => (
            <div key={opt.key} className="flex items-center gap-2 py-0">
              <input
                type="checkbox"
                id={opt.id ?? `checkbox-email-${opt.key}`}
                checked={selectedEmails[opt.key] || false}
                onChange={(e) =>
                  handleEmailCheckboxChange(opt.key, e.target.checked)
                }
              />
              <label
                htmlFor={opt.id ?? `checkbox-email-${opt.key}`}
                className="text-sm cursor-pointer mt-2! p-0 flex-1"
              >
                {opt.label}
              </label>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mb-4 mt-6!">
          <TextInputField
            id="new-email-input"
            name="newEmail"
            label="Alternate e-mail address"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value)
              if (errors.newEmail)
                setErrors((prev) => {
                  const { newEmail: _, ...rest } = prev
                  return rest
                })
            }}
            placeholder="i.e. you@example.com"
            type="email"
            error={errors.newEmail}
            helperText="Edit field; Must include '@' and '.com'."
            showHelper={true}
            customStyles={{ width: '80%', fontSize: '16px', fontWeight: 'normal' }}
          />
          <TextInputField
            id="reenter-email-input"
            name="reenteredEmail"
            label="Please type your e-mail address again."
            value={reenteredEmail}
            onChange={(e) => {
              setReenteredEmail(e.target.value)
              if (errors.reenteredEmail)
                setErrors((prev) => {
                  const { reenteredEmail: _, ...rest } = prev
                  return rest
                })
            }}
            placeholder="i.e. you@example.in.com"
            type="email"
            error={errors.reenteredEmail}
            helperText="Edit field; Must end with .in.com or .org"
            showHelper={true}
            customStyles={{ width: '80%', fontSize: '16px', fontWeight: 'normal' }}
          />
        </div>

        <div className="flex flex-col desktop:flex-row gap-3 mt-6!">
          <button type="button"
            className="button -secondary w-full desktop:w-auto px-4 py-2 border border-[#0074AD] text-[#0074AD] rounded-sm font-semibold text-sm hover:bg-[#F0F4F7]"
            onClick={handleReset}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button type="button"
            className="button -primary -light w-full desktop:w-auto px-4 py-2 bg-[#0074AD] text-white rounded-sm font-semibold text-sm hover:bg-[#005A8A]"
            onClick={handleSave}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

export const EmployeeInfo: React.FC<{
  /**
   * Set by a parent that already dispatches getEmployeeInfo itself (e.g.
   * RequestsContainer, which needs the result before it knows whether to
   * render this component at all) so this component reads the resulting
   * redux state without firing a second, redundant fetch. Defaults to false
   * so every other existing usage of this component is unaffected.
   */
  skipFetch?: boolean
}> = ({ skipFetch = false }) => {
  const dispatch = useAppDispatch()
  const manualSelected = useAppSelector(s => s.manualTimeOff?.selectedEmployee)
  const { data, loading, error: reduxError } = useAppSelector(s => s.employeeInfo.employeeInfo)
  const regionCd = useAppSelector((s) => s.profile?.data?.employee?.regionCd)
  const isScalUser = (regionCd || '').trim().toUpperCase() === SCAL_REGION_CD
  const [employeeDetailsOpen, setEmployeeDetailsOpen] = useState(false)
  const [showEmailCard, setShowEmailCard] = useState(false)
  const [patchLoading, setPatchLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const envConfig = getEnvironmentConfig() as any
  const tortSharePointUrl = envConfig.TortSharePointUrl || ''
  const tortTrainingUrl = envConfig.TortKpLearnUrl || ''
  const timeOffPoliciesUrl = envConfig.TimeOffPoliciesUrl || ''
  const configuredHrConnectUrl = envConfig.HRConnectUrl || ''
  // Fetches whichever employee the X-emp-nuid header currently resolves to
  // (the logged-in user, or the manually-selected employee when a proxy is
  // acting on their behalf) — never the login profile itself.
  //
  // Aborting the in-flight request on cleanup matters here specifically:
  // switching from one selected employee to another (without an unmount in
  // between) dispatches a new request before the previous one necessarily
  // resolves. The reducer applies whichever response arrives last with no
  // staleness check, so an out-of-order response for the PREVIOUS employee
  // could silently overwrite the correct data for the one actually selected.
  useEffect(() => {
    if (skipFetch) return
    const promise = dispatch(getEmployeeInfo())
    return () => {
      promise?.abort?.()
    }
  }, [dispatch, manualSelected?.nuid, skipFetch])

  const handleEmailPreferencesSave = async (payload: {
    kpSelected: boolean
    alternateSelected: boolean
    alternateEmail?: string
  }) => {
    try {
      setErrorMessage(null)
      setPatchLoading(true)
      const result = await dispatch(updateEmployeePreferences(payload))
      
      // Check if patch was successful
      if (updateEmployeePreferences.rejected.match(result)) {
        const errorMsg = result.payload as string
        setErrorMessage(errorMsg)
        throw new Error(errorMsg)
      }
      
      // Refetch employee info after successful patch
      const getResult = await dispatch(getEmployeeInfo())

      if (getEmployeeInfo.rejected.match(getResult)) {
        const errorMsg = getResult.payload as string
        setErrorMessage(errorMsg)
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('handleEmailPreferencesSave error:', error)
      throw error
    } finally {
      setPatchLoading(false)
    }
  }

  const NO_EMAIL_MSG =
    'There is no email selected or on file. Email notifications will not be sent.'

  // Get preferred email(s) based on new API structure
  const getPreferredEmails = () => {
    const emails = []
    // Show kpEmail only if kpSelected is true
    if (data?.kpSelected && data?.kpEmail) {
      emails.push(data.kpEmail)
    }
    // Additionally show alternate email if selected
    if (data?.alternateSelected && data?.alternateEmail) {
      emails.push(data.alternateEmail)
    }
    return emails
  }

  const preferredEmails = getPreferredEmails()
  const hasPreferredEmail = preferredEmails.length > 0

  const emailDisplay = () => {
    if (!hasPreferredEmail) {
      return (
        <div className="flex items-center gap-2">
          <img src={errorIcon} alt="error icon"/>
          <span className="text-red-600 p3 -book">{NO_EMAIL_MSG}</span>
        </div>
      )
    }
    
    return (
      <div className="flex flex-col">
        {preferredEmails.map((email) => (
          <p className='p3 -book mb-0! mt-0!' key={email}>
            {email}
          </p>
        ))}
      </div>
    )
  }

  const details = data ? [
    { id: 'job', label: 'Job', value: data?.job || 'N/A' },
    { id: 'union-seniority-date', label: 'Union Seniority Date', value: data?.unionSeniorityDate || 'NA' },
    { id: 'alternate-seniority-date', label: 'Alternate Seniority Date', value: data?.alternateSeniorityDate || 'NA' },
    { id: 'union', label: 'Union', value: data?.union || 'N/A' },
    { id: 'department', label: 'Department', value: data?.department || 'N/A' },
    { id: 'location', label: 'Location', value: data?.location || 'N/A' },
    { id: 'region', label: 'Region', value: data?.regionName || 'N/A' },
    { id: 'shift', label: 'Shift', value: data?.shift || 'N/A' },
  ] : []

  const createRequestPath = globalThis.location.pathname.startsWith('/admin/requestor')
    ? '/admin/requestor/create'
    : '/requestor/create'

  const resources = [
    { id: 'create-request', title: 'Create a new TORT request', link: createRequestPath },
    { id: 'sharepoint', title: 'TORT SharePoint site', link: tortSharePointUrl },
    { id: 'take-training', title: 'Take KpLearn Employee TORT training', link: tortTrainingUrl },
    { id: 'job-aid', title: 'Review TORT Employee Job Aid', link: '/assets/documents/tort-requestor-job-aid.pdf' },
    { id: 'questions-qa', title: 'Questions? View FAQS', link: '/assets/documents/tort-employee-faq.pdf',class:"h2" },
    { id: 'review-policies', title: 'Review time-off policies', link: timeOffPoliciesUrl },
    ...(isScalUser ? [{ id: 'super-contact', title: 'SCAL Super User contact list', link: '/assets/documents/scal-super-user-contact-list.xls' }] : []),
  ]

  if (loading) {
    return (
      <div className="mt-6 p-5">
        <Loader />
      </div>
    )
  }

  const validHrConnectUrl = configuredHrConnectUrl

  return (
    <>
      {patchLoading && <Loader />}

      <div
        className="mt-6! ds-card-group grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2 gap-4 tablet:gap-3 mobile:gap-2 items-start"
        data-ds-theme="vessel"
        data-ds-variant="basic"
        data-ds-version="2"
      >
        <div
          className="ds-card"
          data-ds-theme="vessel"
          data-ds-variant="basic"
          data-ds-version="2"
          id={`card-1`}
          aria-labelledby={`card-1-title`}
        >
          <div className="ds-card__content p-4 tablet:p-3 mobile:p-2 flex flex-col">
            <h2
              className="h2 text-xl tablet:text-lg mobile:text-base m-0"
              id={`card-1-title`}
            >
              Employee Information
            </h2>

            {reduxError && (
              <div className="mt-3 max-w-md">
                <ValidationBanner
                  variant="error"
                  heading={reduxError}
                  message="Please try again or contact support if the issue persists."
                />
              </div>
            )}

            {!reduxError && (
              <>
                <p className="p3 -medium m-0! py-0! leading-normal">
                  Employee (NUID)
                </p>
                <p className="p3 -book m-0! py-0! leading-normal">
                  {data?.employeeName ?? ''}
                </p>
                <p className="p3 -medium mt-4! m-0! py-0! leading-normal">
                  Preferred e-mail Address
                </p>
                {emailDisplay()}
                <button
                  className="flex items-center gap-2 bg-transparent ml-2! border-0 cursor-pointer p-0 hover:opacity-80"
                  onClick={() => setShowEmailCard(!showEmailCard)}
                  aria-expanded={showEmailCard}
                >
                  <img
                    src={showEmailCard ? minusIcon : plusIcon}
                    alt={showEmailCard ? 'Collapse' : 'Expand'}
                    className="inline-block"
                  />
                  <p className="p3 -medium text-[#003B71] text-sm m-0">Edit</p>
                </button>

                {errorMessage && (
                  <div className="mt-3 max-w-md">
                    <ValidationBanner
                      variant="error"
                      heading={errorMessage}
                      message="Please try again or contact support if the issue persists."
                    />
                  </div>
                )}

                {showEmailCard && (
                  <EmailCardSection
                    showEmailCard={showEmailCard}
                    setShowEmailCard={setShowEmailCard}
                    currentEmail={data?.kpEmail || ''}
                    prefilledAlternateEmail={data?.alternateEmail || undefined}
                    emailOptions={[
                      {
                        key: 'kp',
                        label: data?.kpEmail || 'KP Email',
                        id: 'checkbox-email-kp',
                        checked: data?.kpSelected || false,
                        requiresNewEmail: false,
                      },
                      {
                        key: 'alternate',
                        label: 'Alternative e-mail address',
                        id: 'checkbox-email-alternate',
                        checked: data?.alternateSelected || false,
                        requiresNewEmail: !data?.alternateEmail, // Only require new email if no alternate email provided
                      },
                    ]}
                    onSave={handleEmailPreferencesSave}
                    isLoading={patchLoading}
                  />
                )}

                <div className="mt-4!">
                  <p className="p3 -medium m-0! py-0!">Primary Approver</p>
                  <p className="p3 -book m-0! py-0!">
                    {data?.primaryApprover || 'NA'}
                  </p>
                  <p className="p3 -book mt-6!">
                    To update your KP TIME Primary Approver please have your manager contact the KP TIME system at{' '}
                    <a
                      href="mailto:NPS-National-Timekeeping@kp.org"
                      className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
                    >
                      NPS-National-Timekeeping@kp.org
                    </a>
                  </p>
                </div>

                <button
                  type="button"
                  className="flex items-center gap-2 ml-2! mt-3!"
                  onClick={() => setEmployeeDetailsOpen((s) => !s)}
                  aria-expanded={employeeDetailsOpen}
                  aria-controls={`card-1-details`}
                >
                  <img
                    src={employeeDetailsOpen ? minusIcon : plusIcon}
                    alt={employeeDetailsOpen ? 'Minus Icon' : 'Plus Icon'}
                    className="inline-block"
                  />
                  <span className=" text-[#003B71] p3 -medium">
                    More Employee Information
                  </span>
                </button>

                {employeeDetailsOpen && (
                  <div
                    id={`card-1-details`}
                    className="flex flex-col ml-10! gap-2!"
                  >
                    {details.map((d) => (
                      <div key={d.id}>
                        <LabeledValue
                          label={d.label.replace(/:\s*$/, '')}
                          value={d.value}
                        />
                      </div>
                    ))}

                    <p className="p3 -medium text-xs m-0">
                      Employee information provided by     {validHrConnectUrl ? (
                        <a
                          href={validHrConnectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-[#0074AD]"
                        >
                          HRconnect
                        </a>
                      ) : (
                        <span className="text-[#0074AD]">HRconnect</span>
                      )}. Contact your
                      approver to correct any inaccurate information.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {!reduxError && (
        <div
          className="ds-card"
          data-ds-theme="vessel"
          data-ds-variant="basic"
          data-ds-version="2"
          id={`card-2`}
          aria-labelledby={`card-2-title`}
        >
          <div className="ds-card__content p-4 tablet:p-3 mobile:p-2">
            <h2
              className="h2 text-xl tablet:text-lg mobile:text-base"
              id={`card-2-title`}
            >
              Helpful Resources
            </h2>
            <ul
              style={{ marginTop: 0 }}
              className="list-disc pl-5 ml-4! marker:text-[#0074AD]"
            >
              {resources.map((r) => (
                <li key={r.id} className="text-sm mt-2!">
                  {(() => {
                    let resourceContent

                    if (r.id === 'create-request') {
                      resourceContent = (
                        <a
                          href={r.link}
                          className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
                        >
                          {r.title}
                        </a>
                      )
                    } else if (r.link) {
                      resourceContent = (
                        <a
                          href={r.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0074AD] hover:text-[#0074AD]! transition-none!"
                        >
                          {r.title}
                        </a>
                      )
                    } else {
                      resourceContent = (
                        <span className="text-[#0074AD]">{r.title}</span>
                      )
                    }

                    return resourceContent
                  })()}
                </li>
              ))}
            </ul>
            <h2
              className="h2 text-xl tablet:text-lg mobile:text-base"
              id={`card-2-title`}
            >
              Questions?
            </h2>
            <p className="p3 -book text-xs mt-4">
              Please follow up with your TORT authorized approver for general
              TORT questions. For escalated TORT issues, please contact the
              National TORT Support team at{' '}
              <a href="mailto:NAT-TORT@kp.org" className="whitespace-nowrap text-[#0074AD]">
                NAT-TORT@kp.org
              </a>
              {'. '}
              Please include your name, employee ID, NUID, computer screenshots,
              and a description of your issue.
            </p>
          </div>
        </div>
        )}
      </div>
    </>
  )
}
